'use client';

import {
  Button,
  CardTable,
  Text,
  Input,
  ButtonGroup,
  Checkbox,
  Pill,
  Alert,
  Dialog,
} from '@/_components';
import {
  FC,
  useCallback,
  useContext,
  useState,
  useRef,
  useEffect,
} from 'react';
import { UserContext } from '@/_contexts/UserProvider';
import { ToastContext } from '@/_contexts/ToastProvider';
import {
  updateTaxReturnK1Packages,
  sendK1PackageToPartner,
} from '@/_api/client-tax-returns';
import type { K1Package } from '@/_types/models/taxReturns';
import {
  getK1PackageStatusInfo,
  isK1PackageEditable,
  isK1PackageSendable,
  isK1PackageMarkDeliverable,
} from '@/_types/models/taxReturns';
import styles from './ClientK1DistributionDialog.module.css';
import { PreviewContext } from '@/_contexts/PreviewProvider';
import { downloadDocument } from '@/_api/client-documents';
import type { ClientK1DistributionDialogProps } from './ClientK1DistributionDialog.types';

import { truncateString } from '@/_utils/strings';
import { saveAs } from '@/_utils/files';

export const ClientK1DistributionDialog: FC<
  ClientK1DistributionDialogProps
> = ({ taxReturn, isOpen, onClose, onTaxReturnUpdate }) => {
  const { token } = useContext(UserContext);
  const { addToast } = useContext(ToastContext);
  const { setPreviewFile } = useContext(PreviewContext);

  const [isSaving, setIsSaving] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [selectedPackages, setSelectedPackages] = useState<Set<string>>(
    new Set(),
  );

  // Local state for immediate UI updates
  const [localTaxReturn, setLocalTaxReturn] = useState(taxReturn);

  // Update local state when prop changes
  useEffect(() => {
    setLocalTaxReturn(taxReturn);
  }, [taxReturn]);

  // Simple timeout-based saving
  const saveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Cleanup timeout on component unmount to prevent memory leaks
  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, []);

  const getStatusInfo = (pkg: K1Package) => {
    const statusInfo = getK1PackageStatusInfo(pkg);
    return statusInfo;
  };

  const getDistributeButtonInfo = (pkg: K1Package) => {
    if (pkg.consented_to_electronic_delivery === false) {
      return {
        isDisabled: true,
        tooltip: 'Partner has declined electronic delivery',
      };
    } else if (pkg.sent_at_to_partner) {
      return {
        isDisabled: false,
        tooltip: 'Resend',
      };
    }

    if (!pkg.partner_email || pkg.partner_email.trim() === '') {
      return {
        isDisabled: true,
        tooltip: 'Partner email is required',
      };
    }

    if (!pkg.partner_name || pkg.partner_name.trim() === '') {
      return {
        isDisabled: true,
        tooltip: 'Partner name is required',
      };
    }

    return {
      isDisabled: false,
      tooltip: 'Send',
    };
  };

  const handleUpdatePackage = useCallback(
    async (
      packageToUpdate: K1Package,
      field: keyof K1Package,
      value: string,
    ) => {
      if (!token || !localTaxReturn?.id || !localTaxReturn?.company_id) {
        return;
      }

      try {
        const updatedPackages = localTaxReturn.k1_packages.map((pkg) =>
          pkg.document_path === packageToUpdate.document_path
            ? { ...pkg, [field]: value }
            : pkg,
        );

        await updateTaxReturnK1Packages({
          id: localTaxReturn.id,
          companyId: localTaxReturn.company_id,
          token,
          ...{
            k1_packages: updatedPackages,
          },
        });
      } catch (error) {
        addToast('error', 'Failed to update K-1 package', { error });
      }
    },
    [token, localTaxReturn, addToast],
  );

  const handleDistributePackage = useCallback(
    async (packageToDistribute: K1Package) => {
      if (!token || !localTaxReturn?.id || !localTaxReturn?.company_id) {
        return;
      }

      if (
        !packageToDistribute.partner_email ||
        packageToDistribute.partner_email.trim() === ''
      ) {
        addToast('error', 'Partner email is required');
        return;
      }

      if (
        !packageToDistribute.partner_name ||
        packageToDistribute.partner_name.trim() === ''
      ) {
        addToast('error', 'Partner name is required');
        return;
      }

      if (!isK1PackageSendable(packageToDistribute)) {
        addToast('error', 'K-1 package has already been viewed or consented');
        return;
      }

      await handleUpdatePackage(
        packageToDistribute,
        'partner_email',
        packageToDistribute.partner_email,
      );
      await handleUpdatePackage(
        packageToDistribute,
        'partner_name',
        packageToDistribute.partner_name,
      );

      try {
        setIsSaving(true);

        const response = await sendK1PackageToPartner({
          company_id: localTaxReturn.company_id,
          tax_return_id: localTaxReturn.id,
          document_path: packageToDistribute.document_path,
          partner_email: packageToDistribute.partner_email,
          partner_name: packageToDistribute.partner_name,
          token,
        });

        if (response.error) {
          addToast('error', response.error);
          return;
        }

        addToast('success', 'K-1 package sent successfully');
        await onTaxReturnUpdate();
      } catch (error) {
        addToast('error', 'Failed to send K-1 package', { error });
      } finally {
        setIsSaving(false);
      }
    },
    [token, localTaxReturn, addToast, onTaxReturnUpdate, handleUpdatePackage],
  );

  const handleInputChange = useCallback(
    (
      documentPath: string,
      field: 'partner_name' | 'partner_email',
      value: string,
    ) => {
      if (!localTaxReturn) return;

      // Clear any existing timeout
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }

      // Find the package to update
      const packageToUpdate = localTaxReturn.k1_packages?.find(
        (pkg) => pkg.document_path === documentPath,
      );

      if (!packageToUpdate) return;

      // Update local state immediately for UI responsiveness
      const updatedPackages = localTaxReturn.k1_packages.map((pkg) =>
        pkg.document_path === documentPath ? { ...pkg, [field]: value } : pkg,
      );

      // Update the local tax return with new packages
      setLocalTaxReturn({
        ...localTaxReturn,
        k1_packages: updatedPackages,
      });

      // Save to server after a short delay
      saveTimeoutRef.current = setTimeout(async () => {
        try {
          await handleUpdatePackage(packageToUpdate, field, value);
        } catch (error) {
          console.error('Failed to save input change:', error);
          // Revert local state on error
          await onTaxReturnUpdate();
        }
      }, 500); // 500ms delay
    },
    [localTaxReturn, handleUpdatePackage, onTaxReturnUpdate],
  );

  const handlePreviewDocument = useCallback(
    async (documentPath: string) => {
      try {
        if (!token || !localTaxReturn?.company_id) return;
        const { document } = await downloadDocument({
          companyId: localTaxReturn?.company_id!,
          link: documentPath,
          token,
          isDownload: false,
        });
        setPreviewFile(document?.data?.link);
      } catch (error) {
        addToast('error', 'Failed to preview document', { error });
      }
    },
    [token, localTaxReturn?.company_id, setPreviewFile, addToast],
  );

  const handleDownloadDocument = useCallback(
    async (documentPath: string, partnerName: string) => {
      try {
        if (!token || !localTaxReturn?.company_id) return;
        const response = await downloadDocument({
          companyId: localTaxReturn?.company_id!,
          link: documentPath,
          token,
          isDownload: true,
        });
        const fileName =
          partnerName.trim() === ''
            ? 'K-1 package ().pdf'
            : `${partnerName} K-1 package.pdf`;
        saveAs(response.document.data.link, fileName);
        addToast('success', 'K-1 package downloaded successfully');
      } catch (error) {
        addToast('error', 'Failed to download K-1 package', { error });
      }
    },
    [token, localTaxReturn?.company_id, addToast],
  );

  const toggleMarkDelivered = useCallback(
    async (packageToMark: K1Package, isMarkedDelivered: boolean) => {
      if (!token || !localTaxReturn?.id || !localTaxReturn?.company_id) {
        return;
      }

      try {
        setIsSaving(true);

        // Update the package to mark it as delivered (sent) with current timestamp
        const updatedPackages = localTaxReturn.k1_packages.map((pkg) =>
          pkg.document_path === packageToMark.document_path
            ? {
                ...pkg,
                marked_delivered_at: isMarkedDelivered
                  ? Math.floor(Date.now() / 1000)
                  : null,
              }
            : pkg,
        );

        await updateTaxReturnK1Packages({
          id: localTaxReturn.id,
          companyId: localTaxReturn.company_id,
          token,
          ...{
            k1_packages: updatedPackages,
          },
        });

        addToast(
          'success',
          isMarkedDelivered
            ? 'K-1 package marked as delivered'
            : 'K-1 package marked as not delivered',
        );
        await onTaxReturnUpdate();
      } catch (error) {
        addToast(
          'error',
          isMarkedDelivered
            ? 'Failed to mark K-1 package as delivered'
            : 'Failed to mark K-1 package as not delivered',
          { error },
        );
      } finally {
        setIsSaving(false);
      }
    },
    [token, localTaxReturn, addToast, onTaxReturnUpdate],
  );

  const handleSelectPackage = useCallback(
    (packageKey: string, isSelected: boolean) => {
      setSelectedPackages((prev) => {
        const newSet = new Set(prev);
        if (isSelected) {
          newSet.add(packageKey);
        } else {
          newSet.delete(packageKey);
        }
        return newSet;
      });
    },
    [],
  );

  const handleSelectAll = useCallback(
    (isSelected: boolean) => {
      if (isSelected) {
        const allKeys =
          localTaxReturn?.k1_packages?.map((pkg) => pkg.document_path) || [];
        setSelectedPackages(new Set(allKeys));
      } else {
        setSelectedPackages(new Set());
      }
    },
    [localTaxReturn?.k1_packages],
  );

  const isAllSelected = Boolean(
    localTaxReturn?.k1_packages &&
      localTaxReturn.k1_packages.length > 0 &&
      localTaxReturn.k1_packages.every((pkg) =>
        selectedPackages.has(pkg.document_path),
      ),
  );

  const handleBulkDistribute = useCallback(async () => {
    if (!token || !localTaxReturn?.id || !localTaxReturn?.company_id) {
      return;
    }

    try {
      setIsSaving(true);

      const selectedPackagesList = localTaxReturn.k1_packages?.filter((pkg) =>
        selectedPackages.has(pkg.document_path),
      );

      // Filter out packages that don't have email addresses
      const validPackages = selectedPackagesList.filter(
        (pkg) =>
          pkg.partner_email &&
          pkg.partner_email.trim() !== '' &&
          isK1PackageSendable(pkg),
      );

      if (validPackages.length === 0) {
        addToast(
          'error',
          'No valid packages to send (missing email addresses, already viewed, or already declined electronic delivery)',
        );
        return;
      }

      // Send each valid package
      for (const pkg of validPackages) {
        await sendK1PackageToPartner({
          company_id: localTaxReturn.company_id,
          tax_return_id: localTaxReturn.id,
          document_path: pkg.document_path,
          partner_email: pkg.partner_email,
          partner_name: pkg.partner_name,
          token,
        });
      }

      // Clear selection
      setSelectedPackages(new Set());

      addToast(
        'success',
        `Successfully sent ${validPackages.length} K-1 package(s)`,
      );
      await onTaxReturnUpdate();
    } catch (error) {
      addToast('error', 'Failed to send K-1 packages', { error });
    } finally {
      setIsSaving(false);
    }
  }, [token, localTaxReturn, addToast, selectedPackages, onTaxReturnUpdate]);

  const getBulkSendButtonInfo = () => {
    if (selectedPackages.size === 0) {
      return {
        isDisabled: true,
        tooltip: 'Select packages to send',
        label: 'Send Selected',
      };
    }

    const selectedPackagesList =
      localTaxReturn?.k1_packages?.filter((pkg) =>
        selectedPackages.has(pkg.document_path),
      ) || [];

    const validPackages = selectedPackagesList.filter(
      (pkg) =>
        pkg.partner_email &&
        pkg.partner_email.trim() !== '' &&
        isK1PackageSendable(pkg),
    );

    if (validPackages.length === 0) {
      return {
        isDisabled: true,
        tooltip: 'Selected packages missing email addresses',
        label: 'Send Selected',
      };
    }

    return {
      isDisabled: false,
      tooltip: `Send ${validPackages.length} package(s)`,
      label: `Send Selected (${validPackages.length})`,
    };
  };

  const handleRefresh = useCallback(async () => {
    if (!token || !localTaxReturn?.id || !localTaxReturn?.company_id) {
      return;
    }

    try {
      setIsRefreshing(true);

      // Clear any selections when refreshing
      setSelectedPackages(new Set());

      // Trigger a refresh of the tax return data
      await onTaxReturnUpdate();

      addToast('success', 'K-1 packages refreshed successfully');
    } catch (error) {
      addToast('error', 'Failed to refresh K-1 packages', { error });
    } finally {
      setIsRefreshing(false);
    }
  }, [token, localTaxReturn, addToast, onTaxReturnUpdate]);

  const editDisabled = (pkg: K1Package) => {
    return !isK1PackageEditable(pkg);
  };

  const markDeliveredDisabled = (pkg: K1Package) => {
    return !isK1PackageMarkDeliverable(pkg);
  };

  // Don't render if no tax return data
  if (!localTaxReturn) return null;

  return (
    <Dialog
      isOpen={isOpen}
      onClose={onClose}
      title="K-1 Package Distribution"
      isExtraWide={true}
      headerButtons={
        <Button
          intent="ghost"
          onClick={handleRefresh}
          iconLeft="refresh"
          value="Refresh"
          isDisabled={isRefreshing}
          isLoading={isRefreshing}
        />
      }
    >
      <Dialog.Content>
        <Alert type="info" icon="info">
          Review and edit partner or member information for K-1 packages then
          send them to partners or members or mark them as delivered. If the
          partner or member declines to receive the K-1 package electronically,
          you will receive an email to view the K-1 package.
        </Alert>
        <CardTable isLoading={false}>
          <table className={styles.k1Table}>
            <thead>
              <tr>
                <th>
                  <Checkbox
                    label="Select All"
                    isLabelHidden={true}
                    isChecked={isAllSelected}
                    onChange={(e) => handleSelectAll(e.target.checked)}
                  />
                </th>
                <th>Partner Name</th>
                <th>Partner Email</th>
                <th>K-1 Package</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {localTaxReturn?.k1_packages?.map((pkg, index) => {
                const statusInfo = getStatusInfo(pkg);
                const distributeButtonInfo = getDistributeButtonInfo(pkg);
                return (
                  <tr key={index}>
                    <td>
                      <Checkbox
                        label="Select"
                        isLabelHidden={true}
                        isChecked={selectedPackages.has(pkg.document_path)}
                        onChange={(e) =>
                          handleSelectPackage(
                            pkg.document_path,
                            e.target.checked,
                          )
                        }
                      />
                    </td>
                    <td>
                      <Input
                        label="Partner Name"
                        value={pkg.partner_name}
                        onChange={(e) =>
                          handleInputChange(
                            pkg.document_path,
                            'partner_name',
                            e.target.value,
                          )
                        }
                        placeholder="Enter partner name"
                        isLabelHidden={true}
                        isDisabled={editDisabled(pkg)}
                      />
                    </td>
                    <td>
                      <Input
                        label="Partner Email"
                        value={pkg.partner_email}
                        onChange={(e) =>
                          handleInputChange(
                            pkg.document_path,
                            'partner_email',
                            e.target.value,
                          )
                        }
                        placeholder="Enter partner email"
                        type="email"
                        isLabelHidden={true}
                        isDisabled={editDisabled(pkg)}
                      />
                    </td>
                    <td>
                      <Button
                        intent="ghost"
                        onClick={() => handlePreviewDocument(pkg.document_path)}
                        iconLeft="file"
                        value={truncateString(pkg.custom_package_name, 15)}
                        withTooltip={true}
                        tooltipPlacement="bottom"
                        tooltip="Preview"
                      />
                    </td>
                    <td>
                      <Pill
                        label={statusInfo.label}
                        intent={statusInfo.intent}
                      />
                    </td>
                    <td>
                      <ButtonGroup align="left">
                        <Button
                          intent="ghost"
                          onClick={() =>
                            handleDownloadDocument(
                              pkg.document_path,
                              pkg.partner_name,
                            )
                          }
                          iconLeft="download"
                          value="Download"
                          withTooltip={true}
                          tooltipPlacement="bottom"
                          tooltip="Download"
                          isIconOnly={true}
                          size="small"
                        />
                        <Button
                          intent="primary"
                          onClick={() => handleDistributePackage(pkg)}
                          iconLeft="send"
                          value="Send"
                          withTooltip={true}
                          tooltipPlacement="bottom"
                          tooltip={distributeButtonInfo.tooltip}
                          isIconOnly={true}
                          isDisabled={
                            isSaving || distributeButtonInfo.isDisabled
                          }
                          size="small"
                        />
                        {pkg.marked_delivered_at &&
                          !markDeliveredDisabled(pkg) && (
                            <Button
                              intent="danger-ghost"
                              onClick={() => toggleMarkDelivered(pkg, false)}
                              value="Undo"
                              iconLeft="x-circle"
                              isDisabled={isSaving}
                              size="tiny"
                            />
                          )}
                        {!pkg.marked_delivered_at && (
                          <Button
                            intent="ghost"
                            onClick={() => toggleMarkDelivered(pkg, true)}
                            value="Mark Delivered"
                            isDisabled={isSaving || markDeliveredDisabled(pkg)}
                            size="tiny"
                          />
                        )}
                      </ButtonGroup>
                    </td>
                  </tr>
                );
              })}
              {localTaxReturn?.k1_packages?.length === 0 && (
                <tr>
                  <td colSpan={6}>
                    <Text type="small" color="secondary">
                      No K-1 packages uploaded
                    </Text>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </CardTable>
      </Dialog.Content>
      <Dialog.Footer>
        <ButtonGroup align="right">
          <Button
            intent="primary"
            onClick={handleBulkDistribute}
            iconLeft="send"
            value={getBulkSendButtonInfo().label}
            withTooltip={true}
            tooltipPlacement="bottom"
            tooltip={getBulkSendButtonInfo().tooltip}
            isDisabled={isSaving || getBulkSendButtonInfo().isDisabled}
            isLoading={isSaving}
          />
        </ButtonGroup>
      </Dialog.Footer>
    </Dialog>
  );
};
